import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Me } from '@wo/shared';
import { api } from '../lib/api';
import { useSettings } from '../lib/hooks';
import { can } from '../lib/perm';
import { DeviceRequestsBanner, useDeviceRequests } from './DeviceRequests';
import { OfficeClock } from './OfficeClock';

const link = ({ isActive }: { isActive: boolean }) =>
  `rounded-md px-3 py-2 text-sm font-medium ${isActive ? 'bg-slate-900 text-white' : 'text-slate-200 hover:bg-slate-700'}`;

export function Layout({ me, children }: { me: Me; children: ReactNode }) {
  const settings = useSettings();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const devices = can(me, 'devices.manage');
  const waiting = useDeviceRequests(devices).data?.length ?? 0;
  const jobs = can(me, 'jobs.viewAll');

  const logout = async () => {
    await api.post('/auth/logout');
    qc.clear();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <nav className="bg-slate-800">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-1 px-4 py-2">
          <span className="mr-4 font-bold text-white">{settings.data?.appName ?? 'Work Orders'}</span>
          {jobs && <NavLink to="/" end className={link}>Shop Board</NavLink>}
          {can(me, 'jobs.create') && <NavLink to="/work-orders/new" className={link}>New Work Order</NavLink>}
          {jobs && <NavLink to="/work-orders" end className={link}>Work Orders</NavLink>}
          {jobs && <NavLink to="/customers" className={link}>Customers</NavLink>}
          {jobs && <NavLink to="/machines" className={link}>Machines</NavLink>}
          {can(me, 'reports.view', 'timesheets.view') && <NavLink to="/reports" className={link}>Reports</NavLink>}
          {jobs && <NavLink to="/legacy" className={link}>Legacy</NavLink>}
          {can(me, 'void.approve') && <NavLink to="/admin/voids" className={link}>Void Approvals</NavLink>}
          {can(me, 'audit.view') && <NavLink to="/admin/audit" className={link}>Audit Log</NavLink>}
          {devices && (
            <NavLink to="/admin/devices" className={link}>
              Devices
              {waiting > 0 && <span className="ml-1 rounded-full bg-amber-400 px-1.5 text-xs font-bold text-black">{waiting}</span>}
            </NavLink>
          )}
          {can(me, 'users.manage', 'jobs.assign', 'devices.manage') && <NavLink to="/admin/users" className={link}>Users</NavLink>}
          {can(me, 'settings.view', 'users.manage') && <NavLink to="/admin/roles" className={link}>Roles</NavLink>}
          {can(me, 'settings.view') && <NavLink to="/admin/email" className={link}>Email</NavLink>}
          <span className="ml-auto flex items-center gap-3 text-sm text-slate-300">
            {me.tracksHours && <OfficeClock />}
            <span>{me.name} · {me.roleLabel}</span>
          </span>
          <button onClick={logout} className="ml-3 rounded-md px-3 py-2 text-sm text-slate-200 hover:bg-slate-700">
            Sign out
          </button>
        </div>
      </nav>
      {devices && <DeviceRequestsBanner />}
      <main className="mx-auto max-w-[1600px] p-4">{children}</main>
    </div>
  );
}
